import React from 'react';
import s from './app-page-ctas.scss';
import { Box, type ButtonSize } from '@wix/design-system';
import type { AppBadge, ManagedApp } from '@wix/app-market-services';
import type { SupportedAppStatuses } from '@wix/app-market-services-types';
import {
  type AppNotification,
  AppNotificationType,
} from '../app-page-sections/notifications/types';
import { InstallAppButton } from './install-app-button';
import { OpenAppButton } from './open-app-button';
import { type ITranslationContext, useTranslation } from '@wix/app-market-core';
import { MobileInstallationBlockedModal } from './mobile-installation-blocked-modal';
import { UpgradeAppButton } from '../../../components/upgrade-app-button';
import type { AppPageType } from '@wix/app-market-core-components-types';

export interface AppPageCTAsProps {
  managedApp?: ManagedApp;
  status?: SupportedAppStatuses;
  app: {
    id: string;
    instanceId?: string;
    name: string;
    isUpgradable: boolean;
    isAppWithBenefits: boolean;
    isInstalled: boolean;
    isBuiltIn: boolean;
    appBadges: AppBadge[];
    notification: AppNotification | null;
    gTag?: string;
    externalPricingPageUrl?: string;
  };
  size?: ButtonSize;
  dataHook: string;
  isTestMode?: boolean;
  reverseOrder?: boolean;
  isPermissionsRequired?: boolean;
  appPageType: AppPageType;
}

export function AppPageCTAs({
  managedApp,
  app,
  size = 'large',
  dataHook,
  reverseOrder = false,
  isTestMode = false,
  status = 'PUBLISHED',
  isPermissionsRequired = false,
  appPageType,
}: AppPageCTAsProps) {
  const { t } = useTranslation();

  const [isMobileModalOpen, setIsMobileModalOpen] = React.useState(false);

  const sharedButtonProps = {
    origin: dataHook,
    isDisabled: isCtaDisabled(app.notification?.type),
    tooltipContent: getCtaTooltipContent(app.notification, t),
    size,
    openMobileInstallationBlockedModal: () => setIsMobileModalOpen(true),
  };
  return (
    <>
      {isMobileModalOpen && (
        <MobileInstallationBlockedModal
          app={{
            id: app.id,
            name: app.name,
          }}
          onClose={() => setIsMobileModalOpen(false)}
        />
      )}
      <div data-hook={dataHook}>
        {app.isInstalled || app.isBuiltIn ? (
          <Box
            className={reverseOrder ? s.reverseOrder : ''}
            gap="6px"
            flexWrap="nowrap"
          >
            {app.isUpgradable && (
              <Box direction="vertical" flexGrow={1}>
                <UpgradeAppButton
                  app={{
                    id: app.id,
                    instanceId: app.instanceId,
                    appBadges: app.appBadges,
                    externalPricingPageUrl: app.externalPricingPageUrl,
                  }}
                  size={size}
                  origin={dataHook}
                  appPageType={appPageType}
                />
              </Box>
            )}
            <OpenAppButton
              {...sharedButtonProps}
              priority={app.isUpgradable ? 'secondary' : 'primary'}
              app={{
                id: app.id,
                isBuiltIn: app.isBuiltIn,
                appBadges: app.appBadges,
              }}
              managedApp={managedApp}
              appPageType={appPageType}
            />
          </Box>
        ) : (
          <InstallAppButton
            {...sharedButtonProps}
            status={status}
            isTestMode={isTestMode}
            isPermissionsRequired={isPermissionsRequired}
            app={{
              id: app.id,
              isAppWithBenefits: app.isAppWithBenefits,
              appBadges: app.appBadges,
              gTag: app.gTag,
            }}
            appPageType={appPageType}
          />
        )}
      </div>
    </>
  );
}

function getCtaTooltipContent(
  notification: AppNotification | null,
  t: ITranslationContext['t'],
): string {
  if (notification?.type === AppNotificationType.EditorNotSupported) {
    return t('solution.page.isNotAvailable.tooltip', {
      editorType:
        notification.data.editorType === 'EDITORX'
          ? t('editor.x.brand.name')
          : t('wix.studio.brand.name'),
    });
  }

  if (notification?.type === AppNotificationType.DependenciesNotInstalled) {
    return notification.data.appDependencies.length > 1
      ? t('solution.page.appDependenciesNotInstalled.tooltip')
      : t('solution.page.appDependencyNotInstalled.tooltip', {
          appRequirement: notification.data.appDependencies[0].name,
        });
  }

  if (notification?.type === AppNotificationType.DependencyPendingEditor) {
    return t('solution.page.appDependencyPending.tooltip', {
      appRequirement: notification.data.appDependency.name,
    });
  }

  if (notification?.type === AppNotificationType.PremiumSiteRequired) {
    return t('app.page.premium.required.notification.tooltip');
  }

  return '';
}

function isCtaDisabled(notificationType?: AppNotificationType): boolean {
  return Boolean(
    notificationType &&
      [
        AppNotificationType.EditorNotSupported,
        AppNotificationType.DependenciesNotInstalled,
        AppNotificationType.DependencyPendingEditor,
        AppNotificationType.PremiumSiteRequired,
      ].includes(notificationType),
  );
}
